/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Database, Download, DownloadCloud, Trash2, Clock, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

function formatBytes(n) {
  if (!n && n !== 0) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '-';
  try { return format(new Date(iso), "dd MMM yyyy, HH:mm", { locale: it }); } catch { return iso; }
}

function triggerDownload(res, fallbackName) {
  let filename = fallbackName;
  const cd = res.headers['content-disposition'];
  if (cd) {
    const m = /filename="?([^"]+)"?/.exec(cd);
    if (m) filename = m[1];
  }
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function Backup() {
  const [backups, setBackups] = useState([]);
  const [nextRun, setNextRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [schedule, setSchedule] = useState({
    enabled: false, frequency: 'daily', hour: 2, minute: 0, weekday: 0, retention: 7
  });

  const loadAll = useCallback(async () => {
    try {
      const [listRes, schedRes] = await Promise.all([
        axios.get(`${API}/admin/backup/list`),
        axios.get(`${API}/admin/backup/schedule`),
      ]);
      setBackups(listRes.data.backups || []);
      setNextRun(schedRes.data.next_run || listRes.data.next_scheduled_run || null);
      const s = schedRes.data;
      setSchedule({
        enabled: !!s.enabled, frequency: s.frequency || 'daily',
        hour: s.hour ?? 2, minute: s.minute ?? 0, weekday: s.weekday ?? 0, retention: s.retention ?? 7
      });
    } catch {
      toast.error('Errore nel caricamento dei backup');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleExportNow = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/backup/export`, { responseType: 'blob' });
      triggerDownload(res, 'backup.json.gz');
      toast.success('Backup scaricato');
    } catch {
      toast.error('Errore durante il download del backup');
    } finally {
      setExporting(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/admin/backup/run`, {});
      toast.success('Backup creato sul server');
      await loadAll();
    } catch {
      toast.error('Errore durante la creazione del backup');
    } finally {
      setRunning(false);
    }
  };

  const handleDownloadStored = async (b) => {
    try {
      const res = await axios.get(`${API}/admin/backup/${b.id}/download`, { responseType: 'blob' });
      triggerDownload(res, b.filename);
    } catch {
      toast.error('Errore durante il download');
    }
  };

  const handleDelete = async (b) => {
    try {
      await axios.delete(`${API}/admin/backup/${b.id}`);
      toast.success('Backup eliminato');
      await loadAll();
    } catch {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await axios.put(`${API}/admin/backup/schedule`, {
        enabled: schedule.enabled,
        frequency: schedule.frequency,
        hour: Number(schedule.hour),
        minute: Number(schedule.minute),
        weekday: Number(schedule.weekday),
        retention: Number(schedule.retention),
      });
      setNextRun(res.data.next_run || null);
      toast.success('Pianificazione salvata');
    } catch {
      toast.error('Errore nel salvataggio della pianificazione');
    } finally {
      setSavingSchedule(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <Database className="text-[#238A92]" size={26} />
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2A37]" style={{ fontFamily: 'Space Grotesk' }}>Backup Database</h1>
      </div>
      <p className="text-muted-foreground mb-6">Crea, scarica e pianifica i backup del database. Solo amministratori.</p>

      {/* Azioni manuali */}
      <Card className="shadow-[var(--shadow-md)] mb-6">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Backup manuale</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleExportNow} disabled={exporting} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="backup-export-button">
              <Download size={16} className="mr-2" /> {exporting ? 'Preparazione...' : 'Scarica backup ora'}
            </Button>
            <Button onClick={handleRunNow} disabled={running} variant="outline" data-testid="backup-run-button">
              <DownloadCloud size={16} className="mr-2" /> {running ? 'Creazione...' : 'Crea backup sul server'}
            </Button>
            <Button onClick={loadAll} variant="ghost" disabled={loading} title="Aggiorna">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            "Scarica backup ora" genera e scarica subito un file (.json.gz) sul tuo computer.
            "Crea backup sul server" salva una copia sul server, consultabile nella lista qui sotto.
          </p>
        </CardContent>
      </Card>

      {/* Pianificazione */}
      <Card className="shadow-[var(--shadow-md)] mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Clock size={18} className="text-[#238A92]" /> Backup automatico (pianificato)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Abilita backup automatico</Label>
              <p className="text-xs text-muted-foreground">Esegue un backup sul server secondo la pianificazione.</p>
            </div>
            <Switch
              checked={schedule.enabled}
              onCheckedChange={(v) => setSchedule(s => ({ ...s, enabled: v }))}
              data-testid="backup-schedule-toggle"
            />
          </div>

          <Separator />

          <div className={`grid sm:grid-cols-2 gap-4 ${schedule.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
            <div>
              <Label className="mb-1.5 block text-sm">Frequenza</Label>
              <Select value={schedule.frequency} onValueChange={(v) => setSchedule(s => ({ ...s, frequency: v }))}>
                <SelectTrigger data-testid="backup-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Giornaliero</SelectItem>
                  <SelectItem value="weekly">Settimanale</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {schedule.frequency === 'weekly' && (
              <div>
                <Label className="mb-1.5 block text-sm">Giorno della settimana</Label>
                <Select value={String(schedule.weekday)} onValueChange={(v) => setSchedule(s => ({ ...s, weekday: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="mb-1.5 block text-sm">Orario</Label>
              <div className="flex items-center gap-2">
                <Select value={String(schedule.hour)} onValueChange={(v) => setSchedule(s => ({ ...s, hour: Number(v) }))}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => i).map(h => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, '0')}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="font-bold">:</span>
                <Select value={String(schedule.minute)} onValueChange={(v) => setSchedule(s => ({ ...s, minute: Number(v) }))}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 15, 30, 45].map(m => <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground ml-1">(ora italiana)</span>
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block text-sm">Backup da conservare (retention)</Label>
              <Input
                type="number" min={1} max={365}
                value={schedule.retention}
                onChange={(e) => setSchedule(s => ({ ...s, retention: e.target.value }))}
                className="w-32"
                data-testid="backup-retention-input"
              />
              <p className="text-xs text-muted-foreground mt-1">I backup più vecchi oltre questo numero vengono eliminati automaticamente.</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <div className="text-sm">
              {schedule.enabled && nextRun ? (
                <span className="inline-flex items-center gap-1.5 text-[#1E7A58]">
                  <CheckCircle size={15} /> Prossimo backup: <strong>{fmtDate(nextRun)}</strong>
                </span>
              ) : (
                <span className="text-muted-foreground">Backup automatico disattivato</span>
              )}
            </div>
            <Button onClick={handleSaveSchedule} disabled={savingSchedule} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="backup-schedule-save">
              {savingSchedule ? 'Salvataggio...' : 'Salva pianificazione'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista backup */}
      <Card className="shadow-[var(--shadow-md)]">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Backup sul server ({backups.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Caricamento...</p>
          ) : backups.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nessun backup sul server. Crea il primo con "Crea backup sul server".</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Documenti</TableHead>
                    <TableHead>Dimensione</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map(b => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{fmtDate(b.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant={b.trigger === 'scheduled' ? 'secondary' : 'outline'}>
                          {b.trigger === 'scheduled' ? 'Automatico' : 'Manuale'}
                        </Badge>
                      </TableCell>
                      <TableCell>{b.total_documents}</TableCell>
                      <TableCell>{formatBytes(b.size_bytes)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {b.file_exists === false ? (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 mr-2" title="File non più presente sul server">
                              <AlertTriangle size={14} /> file mancante
                            </span>
                          ) : (
                            <Button size="icon" variant="ghost" onClick={() => handleDownloadStored(b)} title="Scarica">
                              <Download size={16} />
                            </Button>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-700" title="Elimina">
                                <Trash2 size={16} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminare questo backup?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Stai per eliminare il backup del {fmtDate(b.created_at)}. Questa azione è irreversibile.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(b)}>Elimina</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            Nota: i file di backup sono salvati sul disco del server. Per conservare una copia sicura nel tempo,
            scarica periodicamente i backup sul tuo computer con "Scarica backup ora".
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
